import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ExpandLess, ExpandMore, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import GRImg from '../../../../../components/GRImg/GRImg'
import { useGetBulkAssignChoicesQuery, useGetBulkAssignCountsQuery, useGetBulkAssignPickerFeaturesQuery } from '../../../../api/implementation-bulk-assign'
import { useGetInternalKeywordsQuery } from '../../../../api/keywords'
import { useGetInternalFeatureTagsQuery } from '../../../../api/screenshot-links'
import { useGetInternalConceptsQuery, useGetInternalTagsQuery } from '../../../../api/tags'
import {
  AddFeaturesDialogShow,
  filterDialogConcepts,
  filterDialogFeatures,
  toDialogConcepts,
} from '../../../../components/AddFeaturesDialog/addFeaturesDialogHelpers'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { minFeatureSearchLength } from '../../../../helpers/screenshotLinks'
import { InternalTagType } from '../../../../types/InternalTag'
import {
  countPickedFeatures,
  getPickerFeatureCount,
  isChoiceByChoiceFeature,
  isFeatureFullyPicked,
  isFeaturePicked,
  toggleFeatureChoices,
  toggleValue,
  toPickerFeatures,
} from '../../helpers/bulkAssignHelpers'
import { BulkAssignPickerFeature, BulkAssignSelection } from '../../types/ImplementationBulkAssign'

type BulkAssignPickerDialogProps = {
  open: boolean
  selection: BulkAssignSelection
  onClose: () => void
  onDone: (selection: BulkAssignSelection) => void
}

const CategoryIcon: FC<{ url?: string; name: string }> = ({ url, name }) =>
  url ? (
    <Box component="span" sx={{ display: 'inline-flex', width: 24, height: 24, mr: 1, '& img': { width: 24, height: 24, objectFit: 'contain' } }}>
      <GRImg src={url} alt={name} />
    </Box>
  ) : null

export const BulkAssignPickerDialog: FC<BulkAssignPickerDialogProps> = ({ open, selection, onClose, onDone }) => {
  const { t } = useTranslation()
  const pickerQuery = useGetBulkAssignPickerFeaturesQuery(undefined, { skip: !open })
  const choicesQuery = useGetBulkAssignChoicesQuery(undefined, { skip: !open })
  const countsQuery = useGetBulkAssignCountsQuery(undefined, { skip: !open })
  const featureTagsQuery = useGetInternalFeatureTagsQuery(undefined, { skip: !open })
  const keywordsQuery = useGetInternalKeywordsQuery(undefined, { skip: !open })
  const conceptsQuery = useGetInternalConceptsQuery(undefined, { skip: !open })
  const tagsQuery = useGetInternalTagsQuery(undefined, { skip: !open })
  const [search, setSearch] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [show, setShow] = useState<AddFeaturesDialogShow>('all')
  const [choiceIds, setChoiceIds] = useState<number[]>([])
  const [conceptIds, setConceptIds] = useState<string[]>([])
  const [expandedLegacyIds, setExpandedLegacyIds] = useState<number[]>([])

  useEffect(() => {
    if (!open) return
    setSearch('')
    setSelectedTagIds([])
    setShow('all')
    setChoiceIds(selection.choiceIds)
    setConceptIds(selection.conceptIds)
    setExpandedLegacyIds([])
    // The dialog starts from the selection it was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const features = useMemo(
    () =>
      pickerQuery.data && choicesQuery.data
        ? toPickerFeatures(
            pickerQuery.data.categories,
            pickerQuery.data.featuresByCategory,
            choicesQuery.data.map(({ legacyId }) => legacyId)
          )
        : [],
    [pickerQuery.data, choicesQuery.data]
  )
  const featureTags = useMemo(
    () => [...(featureTagsQuery.data ?? [])].filter((tag) => tag.active !== false).sort((a, b) => a.name.localeCompare(b.name)),
    [featureTagsQuery.data]
  )
  const concepts = useMemo(
    () =>
      toDialogConcepts(
        conceptsQuery.data ?? [],
        (tagsQuery.data ?? []).filter((tag) => tag.type === InternalTagType.Concept)
      ),
    [conceptsQuery.data, tagsQuery.data]
  )
  const conceptCounts = useMemo(
    () => new Map((conceptsQuery.data ?? []).map((concept) => [concept.id, concept.screenshotIds?.length ?? 0])),
    [conceptsQuery.data]
  )

  // As legacy: a search of 2 or more characters wins over the tags, which match the features' own tags.
  const shownFeatures = useMemo(() => {
    if (show === 'concepts') return []
    if (search.trim().length >= minFeatureSearchLength) {
      return filterDialogFeatures(features, { search, selectedTagIds: [], featureTags: featureTagsQuery.data ?? [], keywords: keywordsQuery.data ?? [] })
    }
    if (selectedTagIds.length) return features.filter((feature) => feature.tags.some((id) => selectedTagIds.includes(id)))
    return features
  }, [show, features, search, selectedTagIds, featureTagsQuery.data, keywordsQuery.data])
  const shownConcepts = useMemo(
    () => (show === 'features' ? [] : filterDialogConcepts(concepts, { search, selectedTagIds })),
    [show, concepts, search, selectedTagIds]
  )
  const categories = useMemo(() => {
    const byCategory = new Map<string, BulkAssignPickerFeature[]>()
    shownFeatures.forEach((feature) => byCategory.set(feature.categoryId, [...(byCategory.get(feature.categoryId) ?? []), feature]))
    return Array.from(byCategory.values())
  }, [shownFeatures])

  const isLoading =
    pickerQuery.isLoading || choicesQuery.isLoading || featureTagsQuery.isLoading || keywordsQuery.isLoading || conceptsQuery.isLoading || tagsQuery.isLoading
  const failed = pickerQuery.isError || choicesQuery.isError || featureTagsQuery.isError || keywordsQuery.isError || conceptsQuery.isError || tagsQuery.isError
  const pickedFeatureCount = countPickedFeatures(features, choiceIds)
  const examples = (count?: number) => (count === undefined ? undefined : t('internal-analysts:ie_bulk_assign_examples', { count }))

  const renderFeature = (feature: BulkAssignPickerFeature) => {
    const picked = isFeaturePicked(feature, choiceIds)
    const fullyPicked = isFeatureFullyPicked(feature, choiceIds)
    const byChoice = isChoiceByChoiceFeature(feature.legacyId)
    const expanded = expandedLegacyIds.includes(feature.legacyId) || (byChoice && picked)
    const count = getPickerFeatureCount(feature, choiceIds, countsQuery.data)
    return (
      <Box key={feature.legacyId}>
        <ListItemButton
          sx={{ py: 0 }}
          aria-expanded={byChoice ? expanded : undefined}
          onClick={() =>
            byChoice ? setExpandedLegacyIds((ids) => toggleValue(ids, feature.legacyId)) : setChoiceIds((ids) => toggleFeatureChoices(ids, feature))
          }
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Checkbox
              edge="start"
              size="small"
              checked={fullyPicked}
              indeterminate={picked && !fullyPicked}
              tabIndex={-1}
              disableRipple
              inputProps={{ 'aria-label': feature.name }}
            />
          </ListItemIcon>
          <ListItemText
            primary={feature.name}
            secondary={byChoice ? [examples(count), t('internal-analysts:ie_bulk_assign_pick_choices')].filter(Boolean).join(' · ') : examples(count)}
          />
          {byChoice && (expanded ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
        {byChoice && (
          <Collapse in={expanded} unmountOnExit>
            <List dense disablePadding aria-label={t('internal-analysts:ie_bulk_assign_choices_of', { name: feature.name })} sx={{ pl: 5 }}>
              {feature.choices.map((choice) => (
                <ListItemButton key={choice.weightId} sx={{ py: 0 }} onClick={() => setChoiceIds((ids) => toggleValue(ids, choice.weightId))}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={choiceIds.includes(choice.weightId)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-label': choice.choice }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={choice.choice}
                    secondary={countsQuery.data ? examples(countsQuery.data.byChoice[String(choice.weightId)] ?? 0) : undefined}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        )}
      </Box>
    )
  }

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-analysts:ie_bulk_assign_picker_title')}
      onClose={onClose}
      onSave={() => onDone({ choiceIds, conceptIds })}
      saveDisabled={!choiceIds.length && !conceptIds.length}
      saveLabel={t('internal-analysts:ie_bulk_assign_picker_done')}
      maxWidth="md"
      dividers
      extraActions={
        <>
          <Button
            disabled={!choiceIds.length && !conceptIds.length}
            onClick={() => {
              setChoiceIds([])
              setConceptIds([])
            }}
          >
            {t('internal-analysts:ie_bulk_assign_picker_clear')}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            {t('internal-analysts:ie_bulk_assign_picker_selected', { features: pickedFeatureCount, concepts: conceptIds.length })}
          </Typography>
        </>
      }
    >
      {isLoading ? (
        <Skeleton variant="rectangular" height={320} />
      ) : (
        <Stack spacing={2}>
          {failed && <Typography color="error.main">{t('internal-analysts:ie_bulk_assign_picker_load_failed')}</Typography>}
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('internal-analysts:ie_bulk_assign_picker_search')}
            inputProps={{ 'aria-label': t('internal-analysts:add_features_search_label') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {t('internal-analysts:add_features_tags_help')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {featureTags.map((tag) => {
                const selected = selectedTagIds.includes(tag.id)
                return (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    aria-pressed={selected}
                    onClick={() => setSelectedTagIds((ids) => toggleValue(ids, tag.id))}
                  />
                )
              })}
            </Stack>
          </Box>
          <RadioGroup row value={show} onChange={(event) => setShow(event.target.value as AddFeaturesDialogShow)}>
            <FormControlLabel value="all" control={<Radio size="small" />} label={t('internal-analysts:add_features_show_all')} />
            <FormControlLabel value="features" control={<Radio size="small" />} label={t('internal-analysts:add_features_show_features')} />
            <FormControlLabel value="concepts" control={<Radio size="small" />} label={t('internal-analysts:add_features_show_concepts')} />
          </RadioGroup>
          {show !== 'concepts' && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('internal-analysts:add_features_features', { count: shownFeatures.length })}
              </Typography>
              <List dense disablePadding aria-label={t('internal-analysts:add_features_features_list')}>
                {categories.map((categoryFeatures) => (
                  <Box key={categoryFeatures[0].categoryId}>
                    <ListSubheader disableSticky sx={{ display: 'flex', alignItems: 'center', px: 0, lineHeight: 2.5 }}>
                      <CategoryIcon url={categoryFeatures[0].iconUrl} name={categoryFeatures[0].categoryName} />
                      {categoryFeatures[0].categoryName}
                    </ListSubheader>
                    {categoryFeatures.map(renderFeature)}
                  </Box>
                ))}
              </List>
            </Box>
          )}
          {show !== 'features' && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('internal-analysts:add_features_concepts', { count: shownConcepts.length })}
              </Typography>
              <List dense disablePadding aria-label={t('internal-analysts:add_features_concepts_list')}>
                {shownConcepts.map((concept) => (
                  <ListItemButton key={concept.id} sx={{ py: 0 }} onClick={() => setConceptIds((ids) => toggleValue(ids, concept.id))}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={conceptIds.includes(concept.id)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-label': concept.name }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={concept.name} secondary={examples(conceptCounts.get(concept.id) ?? 0)} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      )}
    </InternalFormDialog>
  )
}
